import React, { useState, useEffect } from 'react';
import { Link, NavLink } from "react-router-dom"; // Import NavLink
import { 
  GraduationCap, MapPin, Menu, X, Filter, ChevronDown, 
  Star, Heart, Download, CheckCircle, ArrowRight, 
  SlidersHorizontal, ChevronRight, Share2, Search, 
  Eye, FileText, ExternalLink, Loader2 
} from 'lucide-react';

export default function CollegeListingPage() {
  // --- UI STATES ---
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // --- BROCHURE MODAL STATE ---
  const [viewBrochureUrl, setViewBrochureUrl] = useState(null); 
  const [isPdfLoading, setIsPdfLoading] = useState(true); 

  // --- DATA STATES ---
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ locations: [], courses: [] });

  // --- PAGINATION STATES ---
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // --- SELECTED FILTERS STATE ---
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);

  // CONSTANTS
  const ADMIN_WHATSAPP_NUMBER = "919876543210"; 

  // =========================================
  // 1. FETCH FILTER OPTIONS
  // =========================================
  useEffect(() => {
    fetch('https://map-your-college.siteflow.in/admin/get_filters.php')
      .then(res => res.json())
      .then(data => {
        setFilterOptions({
          locations: data.locations || [],
          courses: data.courses || []
        });
      })
      .catch(err => console.error("Error loading filters:", err));
  }, []);

  // =========================================
  // 2. FETCH COLLEGES
  // =========================================
  useEffect(() => {
    setLoading(true);
    const queryParams = new URLSearchParams({
      page: currentPage,
      location: selectedLocations.join(','),
      course: selectedCourses.join(',')
    });

    fetch(`https://map-your-college.siteflow.in/admin/get_all_colleges.php?${queryParams.toString()}`)
      .then(res => res.json())
      .then(response => {
        setColleges(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalRecords(response.pagination.totalRecords);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error loading colleges:", err);
        setLoading(false);
      });
      
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, selectedLocations, selectedCourses]);

  // =========================================
  // 3. HEADER SCROLL EFFECT
  // =========================================
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // =========================================
  // 4. RESET PDF LOADING STATE
  // =========================================
  useEffect(() => {
    if (viewBrochureUrl) {
      setIsPdfLoading(true);
    }
  }, [viewBrochureUrl]);

  // =========================================
  // 5. HANDLERS
  // =========================================
  
  const handleFilterChange = (category, value) => {
    setCurrentPage(1);
    if (category === 'Location') {
      setSelectedLocations(prev => prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]);
    } else if (category === 'Course') {
      setSelectedCourses(prev => prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]);
    }
  };

  const resetFilters = () => {
    setSelectedLocations([]);
    setSelectedCourses([]);
    setCurrentPage(1);
    setMobileFiltersOpen(false);
  };

  const handleViewBrochure = (url) => {
    if (url && url !== "") {
      setViewBrochureUrl(url); 
    } else {
      alert("Brochure currently unavailable. Please contact us.");
    }
  };

  const handleApplyNow = (collegeName) => {
    const message = `Hello, I am interested in admission at *${collegeName}*. Please send me details.`;
    const url = `https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const FilterCheckbox = ({ category, label, checkedState }) => (
    <label className="flex items-center gap-3 cursor-pointer group mb-2 hover:bg-slate-50 p-1 rounded transition-colors">
      <input 
        type="checkbox" 
        checked={checkedState.includes(label)}
        onChange={() => handleFilterChange(category, label)}
        className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500 cursor-pointer" 
      />
      <span className="text-slate-600 text-sm font-medium group-hover:text-blue-950 transition-colors">{label}</span>
    </label>
  );

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 selection:bg-red-100 selection:text-red-900">
      
      {/* ================= HEADER (UPDATED) ================= */}
      <header 
        className={`fixed w-full z-50 transition-all duration-300 border-b border-transparent ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-slate-100 py-3' : 'bg-blue-950 py-5 border-blue-900'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <GraduationCap className={`w-10 h-10 ${isScrolled ? 'text-blue-950' : 'text-white'} transition-colors`} strokeWidth={2} />
                <div className="absolute top-[18px] left-1/2 -translate-x-1/2"><MapPin className="w-4 h-4 text-red-600 fill-red-600" /></div>
              </div>
              <div className="flex flex-col">
                <span className={`text-2xl font-bold tracking-tight leading-none ${isScrolled ? 'text-blue-950' : 'text-white'}`}>
                  Map<span className={`${isScrolled ? 'text-blue-800' : 'text-blue-200'}`}>Your</span>College
                </span>
                <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase mt-1">Nurturing Dreams, Creating Leaders</span>
              </div>
            </Link>
    
            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {[
                { name: 'Home', path: '/' },
                { name: 'Colleges', path: '/colleges' },
                { name: 'About Us', path: '/about' },
                { name: 'Services', path: '/services' },
                { name: 'Contact', path: '/contact' }
              ].map((link) => (
                 <NavLink 
                    key={link.name} 
                    to={link.path} 
                    className={({ isActive }) => 
                      `text-sm font-semibold transition-colors uppercase tracking-wide hover:text-red-600 ${
                        isActive 
                          ? 'text-red-600' // Active Color
                          : (isScrolled ? 'text-slate-600' : 'text-blue-100') // Normal Color
                      }`
                    }
                 >
                    {link.name}
                 </NavLink>
              ))}
              <Link to="/contact">
                <button className="bg-red-600 text-white px-6 py-2.5 rounded text-sm font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 transform hover:-translate-y-0.5">Get Started</button>
              </Link>
            </nav>
    
            {/* Mobile Menu Button */}
            <button className={`${isScrolled ? 'text-blue-950' : 'text-white'} lg:hidden`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
    
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 w-full bg-white border-b border-slate-100 shadow-xl lg:hidden p-4 flex flex-col gap-4 animate-in slide-in-from-top-2">
            {[
                { name: 'Home', path: '/' },
                { name: 'Colleges', path: '/colleges' },
                { name: 'About Us', path: '/about' },
                { name: 'Services', path: '/services' },
                { name: 'Contact', path: '/contact' }
            ].map((link) => (
                <NavLink 
                  key={link.name} 
                  to={link.path} 
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => 
                    `text-lg font-semibold py-2 border-b border-slate-50 ${
                      isActive ? 'text-red-600' : 'text-blue-950'
                    }`
                  }
                >
                  {link.name}
                </NavLink>
            ))}
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
              <button className="bg-red-600 text-white w-full py-3 rounded font-bold">Get Started</button>
            </Link>
          </div>
        )}
      </header>

      {/* ================= BANNER ================= */}
      <div className="bg-blue-950 text-white pt-32 pb-20 relative overflow-hidden">
         <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl translate-x-10 -translate-y-10"></div>
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-blue-200 text-sm mb-2 flex items-center gap-2">Home <ChevronRight size={12}/> Colleges</p>
            <h1 className="text-3xl md:text-4xl font-bold">Find Your Dream College</h1>
            <p className="text-blue-300 mt-2 max-w-2xl">Showing {totalRecords} results based on your preferences. Compare fees, placement, and ranking.</p>
         </div>
      </div>

      {/* ================= MAIN CONTENT ================= */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-20 pb-20">
        <div className="grid lg:grid-cols-4 gap-8">
          
          {/* ================= SIDEBAR ================= */}
          <aside className="hidden lg:block lg:col-span-1">
             <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6 sticky top-28">
                <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
                   <h3 className="font-bold text-lg text-blue-950 flex items-center gap-2"><Filter size={18} /> Filters</h3>
                   <button onClick={resetFilters} className="text-xs text-red-600 font-bold hover:underline">Reset All</button>
                </div>

                <div className="mb-6 border-b border-slate-100 pb-6">
                  <h4 className="font-bold text-blue-950 mb-3 flex justify-between">Location <ChevronDown size={14}/></h4>
                  <div className="max-h-48 overflow-y-auto pr-2 scrollbar-thin">
                    {filterOptions.locations.length > 0 ? (
                        filterOptions.locations.map((city, idx) => (<FilterCheckbox key={idx} category="Location" label={city} checkedState={selectedLocations} />))
                    ) : <div className="text-xs text-slate-400">Loading locations...</div>}
                  </div>
                </div>

                <div className="mb-6 border-b border-slate-100 pb-6">
                  <h4 className="font-bold text-blue-950 mb-3 flex justify-between">Course <ChevronDown size={14}/></h4>
                  <div className="max-h-48 overflow-y-auto pr-2 scrollbar-thin">
                    {filterOptions.courses.length > 0 ? (
                        filterOptions.courses.map((course, idx) => (<FilterCheckbox key={idx} category="Course" label={course} checkedState={selectedCourses} />))
                    ) : <div className="text-xs text-slate-400">Loading courses...</div>}
                  </div>
                </div>

                <div className="mb-2">
                   <h4 className="font-bold text-blue-950 mb-3">Max Fees</h4>
                   <input type="range" className="w-full accent-blue-950 cursor-pointer" />
                   <div className="flex justify-between text-xs text-slate-500 mt-2 font-semibold"><span>₹ 1L</span><span>₹ 25L+</span></div>
                </div>
             </div>
          </aside>

          {/* ================= LISTING ================= */}
          <div className="col-span-1 lg:col-span-3">
             <div className="lg:hidden mb-4">
                <button onClick={() => setMobileFiltersOpen(true)} className="w-full bg-white py-3 rounded-lg shadow border border-slate-200 font-bold text-blue-950 flex items-center justify-center gap-2">
                    <SlidersHorizontal size={18} /> Filter & Sort
                </button>
             </div>

             <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                <p className="text-slate-600 font-medium text-sm">
                   Page <span className="font-bold text-blue-950">{currentPage}</span> of {totalPages} <span className="mx-2 text-slate-300">|</span> Total <span className="font-bold text-blue-950">{totalRecords}</span> Colleges
                </p>
                <div className="flex items-center gap-3">
                   <span className="text-sm text-slate-500 font-medium">Sort by:</span>
                   <select className="bg-slate-50 border border-slate-200 text-sm rounded-lg px-3 py-2 outline-none focus:border-blue-950 font-semibold text-blue-950 cursor-pointer">
                      <option>Popularity</option>
                      <option>Fees: Low to High</option>
                      <option>Ranking: High to Low</option>
                   </select>
                </div>
             </div>

             {loading ? (
                <div className="space-y-6">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="bg-white rounded-xl h-64 border border-slate-200 animate-pulse p-6"></div>
                  ))}
                </div>
             ) : (
                <div className="space-y-6">
                    {colleges.length === 0 ? (
                      <div className="text-center py-24 bg-white rounded-xl border border-slate-200 shadow-sm">
                        <Search size={32} className="text-slate-400 mx-auto mb-4"/>
                        <h3 className="text-xl font-bold text-blue-950">No colleges found</h3>
                        <button onClick={resetFilters} className="mt-4 bg-blue-950 text-white px-6 py-2 rounded-lg font-bold">Clear All Filters</button>
                      </div>
                    ) : (
                      colleges.map((college) => (
                      <div key={college.id} className="bg-white rounded-xl shadow-md border border-slate-100 overflow-hidden hover:shadow-xl hover:border-blue-200 transition-all duration-300 group">
                          <div className="flex flex-col md:flex-row">
                              <div className="md:w-1/3 relative h-52 md:h-auto overflow-hidden">
                                  <img src={college.image || "https://images.unsplash.com/photo-1562774053-701939374585"} alt={college.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                  <div className="absolute top-3 right-3 z-10">
                                      <button className="bg-white/90 p-2 rounded-full text-slate-400 hover:text-red-500 transition-colors shadow-sm"><Heart size={16} /></button>
                                  </div>
                                  {college.featured && <div className="absolute top-3 left-3 bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow uppercase tracking-wider">Featured</div>}
                                  <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/80 to-transparent p-4">
                                     <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded bg-white flex items-center justify-center font-bold text-blue-950 text-xs shadow">{college.logo}</div>
                                        <span className="text-white text-xs font-bold bg-white/20 backdrop-blur px-2 py-0.5 rounded border border-white/30">{college.rank}</span>
                                     </div>
                                  </div>
                              </div>
                              <div className="md:w-2/3 p-6 flex flex-col justify-between">
                                  <div>
                                      <div className="flex justify-between items-start mb-2">
                                          <h2 className="text-xl font-bold text-blue-950 leading-tight group-hover:text-red-600 transition-colors cursor-pointer">{college.name}</h2>
                                          <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded text-green-700 text-xs font-bold border border-green-100 whitespace-nowrap">{college.rating} <Star size={10} fill="currentColor" /></div>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500 mb-5">
                                          <span className="flex items-center gap-1"><MapPin size={14} className="text-red-500" /> {college.location}</span>
                                          <span className="hidden sm:inline text-slate-300">|</span>
                                          <span className="flex items-center gap-1"><CheckCircle size={14} className="text-blue-600" /> {college.approvedBy}</span>
                                      </div>
                                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-lg border border-slate-100 mb-4">
                                        <div><p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Fees</p><p className="text-sm font-bold text-blue-950">{college.fees}</p></div>
                                        <div><p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Avg Pkg</p><p className="text-sm font-bold text-green-700">{college.avgPackage}</p></div>
                                        <div><p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Highest</p><p className="text-sm font-bold text-blue-950">{college.highestPackage}</p></div>
                                        <div><p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Exam</p><p className="text-sm font-bold text-blue-600 underline decoration-blue-200">{college.exam}</p></div>
                                      </div>
                                      <div className="flex gap-2 mb-4 flex-wrap">
                                        {college.tags && college.tags.map((tag, i) => (<span key={i} className="text-[10px] bg-white border border-slate-200 text-slate-500 px-2 py-0.5 rounded-full">{tag}</span>))}
                                      </div>
                                  </div>
                                  <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-100 mt-auto">
                                      {/* VIEW BROCHURE BUTTON */}
                                      <button 
                                        onClick={() => handleViewBrochure(college.brochureUrl)}
                                        className="flex-1 flex items-center justify-center gap-2 bg-slate-50 text-blue-950 py-2.5 rounded-lg text-sm font-bold hover:bg-slate-200 transition-colors border border-slate-200"
                                      >
                                          <Eye size={16} /> View Brochure
                                      </button>
                                      
                                      <button 
                                        onClick={() => handleApplyNow(college.name)}
                                        className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white py-2.5 rounded-lg text-sm font-bold hover:bg-red-700 transition-colors shadow-lg shadow-red-100"
                                      >
                                          Apply Now <ArrowRight size={16} />
                                      </button>
                                  </div>
                              </div>
                          </div>
                      </div>
                      ))
                    )}
                </div>
             )}

             {/* Pagination (Simplified) */}
             {totalPages > 1 && (
               <div className="mt-12 flex justify-center items-center gap-2">
                  <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 bg-white font-bold disabled:opacity-50">Previous</button>
                  <span className="font-bold text-blue-950 text-sm"> Page {currentPage} of {totalPages} </span>
                  <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 bg-white font-bold disabled:opacity-50">Next</button>
               </div>
             )}
          </div>
        </div>
      </div>

      {/* ================= MOBILE FILTER OVERLAY ================= */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 lg:hidden flex justify-end animate-in fade-in">
          <div className="w-4/5 max-w-sm bg-white h-full shadow-2xl overflow-y-auto slide-in-from-right animate-in">
             <div className="p-4 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white z-10">
                <h3 className="font-bold text-lg text-blue-950">Filters</h3>
                <button onClick={() => setMobileFiltersOpen(false)} className="p-2 bg-slate-100 rounded-full hover:bg-red-100 hover:text-red-600 transition"><X size={20} /></button>
             </div>
             <div className="p-6 space-y-8">
                {/* Locations */}
                <div>
                    <h4 className="font-bold text-blue-950 mb-3">Location</h4>
                    <div className="space-y-2">
                        {filterOptions.locations.map((city, idx) => (<FilterCheckbox key={idx} category="Location" label={city} checkedState={selectedLocations} />))}
                    </div>
                </div>
                {/* Courses */}
                <div>
                    <h4 className="font-bold text-blue-950 mb-3">Course</h4>
                    <div className="space-y-2">
                        {filterOptions.courses.map((course, idx) => (<FilterCheckbox key={idx} category="Course" label={course} checkedState={selectedCourses} />))}
                    </div>
                </div>
                <div className="pt-4 border-t border-slate-100">
                   <button onClick={() => setMobileFiltersOpen(false)} className="w-full bg-blue-950 text-white py-3 rounded-lg font-bold shadow-lg">View Results</button>
                   <button onClick={resetFilters} className="w-full text-red-600 font-bold py-3 mt-2 text-sm">Reset All Filters</button>
                </div>
             </div>
          </div>
        </div>
      )}

      {/* ================= PDF BROCHURE VIEWER MODAL ================= */}
      {viewBrochureUrl && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-5xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden relative">
            
            {/* Modal Header */}
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0 z-10">
               <div className="flex items-center gap-2">
                 <FileText size={20} className="text-red-500" />
                 <span className="font-bold text-lg">College Brochure</span>
               </div>
               <div className="flex items-center gap-3">
                 <a href={viewBrochureUrl} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white flex items-center gap-1 text-sm font-semibold transition-colors">
                    <ExternalLink size={14} /> Download / Open Original
                 </a>
                 <button 
                    onClick={() => setViewBrochureUrl(null)} 
                    className="bg-white/10 hover:bg-red-600 hover:text-white p-2 rounded-full transition-all"
                 >
                    <X size={20} />
                 </button>
               </div>
            </div>

            {/* PDF Viewer Container */}
            <div className="flex-grow bg-slate-100 relative">
               
               {/* --- LOADING SPINNER --- */}
               {isPdfLoading && (
                 <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 z-20">
                    <Loader2 size={48} className="text-red-600 animate-spin mb-4" />
                    <p className="text-slate-500 font-semibold animate-pulse">Loading Document...</p>
                 </div>
               )}

               {/* --- IFRAME (Google Docs Viewer) --- */}
               <iframe 
                  src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(viewBrochureUrl)}`}
                  className="w-full h-full border-0"
                  title="Brochure Viewer"
                  onLoad={() => setIsPdfLoading(false)} // Hides spinner on load
               ></iframe>
            </div>

          </div>
        </div>
      )}

     


    </div>
  );
}